import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ReplicaProvider } from './contexts/ReplicaContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ThemeProvider } from './contexts/ThemeContext';

import AuthModal from './components/AuthModal';
import Header from './components/Header';
import Hero from './components/Hero';
import WhatIsReplica from './components/WhatIsReplica';
import UseCases from './components/UseCases';
import ValuePropositions from './components/ValuePropositions';
import Marketplace from './components/Marketplace';
import Footer from './components/Footer';
import AppLayout from './components/AppLayout';
import PersonalDashboardView from './components/PersonalDashboardView';
import CompanyDashboardView from './components/CompanyDashboardView';
import { useWorkspace } from './contexts/WorkspaceContext';
import MyReplicaView from './components/MyReplicaView';
import CompanyReplicasView from './components/CompanyReplicasView';
import TeamsView from './components/TeamsView';
import HiringFlowView from './components/HiringFlowView';
import BillingView from './components/BillingView';
import CreateReplicaPanel from './components/CreateReplicaPanel';
import ReplicaCreationRitual from './components/ReplicaCreationRitual';
import SettingsView from './components/SettingsView';
import PrivacyPolicy from './components/PrivacyPolicy';

import TrustCenter from './components/TrustCenter';
import PricingPage from './components/PricingPage';
import Contact from './components/Contact';
import BrainComparison from './components/BrainComparison';
import IntegrationsShowcase from './components/IntegrationsShowcase';
import { CookieConsentBanner } from './components/CookieConsent';
import MyDataView from './components/MyDataView';
import DataPermissionsView from './components/DataPermissionsView';
import AuditLogViewer from './components/AuditLogViewer';
import EscalationQueuePage from './pages/EscalationQueuePage';
import { ReplicaTrainingPage } from './pages/ReplicaTrainingPage';
import { DailyProofPanel } from './pages/DailyProofPanel';
import { TaskManagementPage } from './pages/TaskManagementPage';
import MessagesView from './components/MessagesView';
import ReplicaChatView from './components/ReplicaChatView';
import AgentToolsPanel from './components/AgentToolsPanel';
import IntegrationsCatalogView from './components/IntegrationsCatalogView';
import WorkflowsPage from './components/WorkflowsPage';
import DailyRoutinePage from './components/DailyRoutinePage';
import WorkModePage from './components/WorkModePage';
import CommandCenterPage from './components/command-center/CommandCenterPage';
import SovereignOrb from './components/SovereignOrb';
import SessionMemoryView from './components/SessionMemoryView';
import IdentityLinksPanel from './components/IdentityLinksPanel';
import McpToolsPanel from './components/McpToolsPanel';
import EmploymentWorkspaceDetail from './components/EmploymentWorkspaceDetail';
import CreateWorkspaceView from './components/CreateWorkspaceView';
import JoinWorkspaceView from './components/JoinWorkspaceView';
import { ChatProvider } from './contexts/ChatContext';
import SocialNetworkView from './components/SocialNetworkView';
import ReplicaToReplicaChatView from './components/ReplicaToReplicaChatView';
import { TutorialProvider } from './contexts/TutorialContext';
import TutorialWizard from './components/TutorialWizard';
import { navItems as staticNavItems } from './data/siteData';
import { useRouteAnnouncer } from './hooks/useRouteAnnouncer';
import { useAppNavigate, pathToViewKey } from './hooks/useAppNavigate';
import { WAITLIST_MODE } from './config/waitlist.config';
import WaitlistPage from './components/WaitlistPage';
import WhitepaperPage from './components/WhitepaperPage';
import ManifestoPage from './components/ManifestoPage';
import { lazy, Suspense } from 'react';
import SovereignCoreSetup from './components/training/SovereignCoreSetup';
import CognitiveLab from './components/cognitive/CognitiveLab';
import SkillLibrary from './components/cognitive/SkillLibrary';
import BriefcaseView from './components/BriefcaseView';
import IdentityVerificationView from './components/IdentityVerificationView';
import SocialSynthesisView from './components/SocialSynthesisView';
import EmployeeLinkingView from './components/EmployeeLinkingView';
import CognitiveMergeView from './components/CognitiveMergeView';
import AugmentedOrgChart from './components/AugmentedOrgChart';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
const SquircleDashboardDemo = lazy(() => import('./components/squircle/SquircleDashboardDemo'));

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SUPER ADMIN FLOATING PANEL
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const SuperAdminPanel: React.FC<{ onNavigate: (view: string) => void }> = ({ onNavigate }) => {
  const { isSuperAdmin, superAdminViewMode, setSuperAdminViewMode } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!isSuperAdmin) return null;

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="fixed bottom-6 right-6 z-[9999] w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30 flex items-center justify-center hover:scale-110 transition-transform"
        title="Super Admin Panel"
      >
        <span className="text-lg">⚡</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-72 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 border border-amber-500/30 backdrop-blur-xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">⚡</span>
          <span className="text-white font-bold text-sm tracking-wide">SUPER ADMIN</span>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="text-white/80 hover:text-white text-xs px-2 py-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          Minimize
        </button>
      </div>

      {/* Dashboard View Switcher */}
      <div className="bg-slate-900/95 backdrop-blur-xl p-4 space-y-3">
        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Dashboard View</p>
        <div className="flex gap-2">
          <button
            onClick={() => { setSuperAdminViewMode('individual'); onNavigate('dashboard'); }}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
              superAdminViewMode === 'individual'
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40 shadow-lg shadow-blue-500/10'
                : 'bg-slate-800 text-slate-500 border border-slate-700 hover:border-slate-600 hover:text-slate-400'
            }`}
          >
            <span className="block text-base mb-0.5">👤</span>
            Personal
          </button>
          <button
            onClick={() => { setSuperAdminViewMode('company'); onNavigate('dashboard'); }}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
              superAdminViewMode === 'company'
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40 shadow-lg shadow-orange-500/10'
                : 'bg-slate-800 text-slate-500 border border-slate-700 hover:border-slate-600 hover:text-slate-400'
            }`}
          >
            <span className="block text-base mb-0.5">🏢</span>
            Company
          </button>
        </div>

        {/* Quick Navigation */}
        <div className="pt-2 border-t border-slate-800">
          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Quick Jump</p>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { view: 'my-replica', label: 'My Replica', icon: '🤖' },
              { view: 'teams', label: 'Teams', icon: '👥' },
              { view: 'settings', label: 'Settings', icon: '⚙️' },
              { view: 'billing', label: 'Billing', icon: '💳' },
              { view: 'messages', label: 'Messages', icon: '💬' },
              { view: 'data', label: 'Data', icon: '🗄️' },
              { view: 'work-mode', label: 'Work Mode', icon: '💼' },
              { view: 'company-replicas', label: 'Company AI', icon: '🏗️' },
            ].map(item => (
              <button
                key={item.view}
                onClick={() => onNavigate(item.view)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-slate-200 text-[11px] transition-colors"
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Current Mode Indicator */}
        <div className="pt-2 border-t border-slate-800">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            superAdminViewMode === 'individual'
              ? 'bg-blue-500/10 border border-blue-500/20'
              : 'bg-orange-500/10 border border-orange-500/20'
          }`}>
            <div className={`w-2 h-2 rounded-full animate-pulse ${
              superAdminViewMode === 'individual' ? 'bg-blue-400' : 'bg-orange-400'
            }`} />
            <span className={`text-[11px] font-semibold ${
              superAdminViewMode === 'individual' ? 'text-blue-400' : 'text-orange-400'
            }`}>
              Viewing as: {superAdminViewMode === 'individual' ? 'Personal User' : 'Company Admin'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard router: picks Personal vs Company dashboard based on super admin view or workspace
const DashboardRouter: React.FC<{ onNavigate: (view: string) => void }> = ({ onNavigate }) => {
  const { currentWorkspace } = useWorkspace();
  const { isSuperAdmin, superAdminViewMode } = useAuth();

  // Super Admin: use the explicit view mode toggle
  if (isSuperAdmin) {
    if (superAdminViewMode === 'company') {
      return <CompanyDashboardView onNavigate={onNavigate} />;
    }
    return <PersonalDashboardView onNavigate={onNavigate} />;
  }

  // Normal users: determine by workspace
  if (currentWorkspace?.companyId || currentWorkspace?.type === 'COMPANY') {
    return <CompanyDashboardView onNavigate={onNavigate} />;
  }
  return <PersonalDashboardView onNavigate={onNavigate} />;
};

// Dashboard App (authenticated users)
const DashboardApp: React.FC<{ onLogout: () => void; initialView?: string }> = ({ onLogout: _onLogout, initialView }) => {
  const location = useLocation();
  const appNavigate = useAppNavigate();
  const currentView = pathToViewKey(location.pathname) || initialView || 'dashboard';

  // Announce view changes to screen readers
  useRouteAnnouncer(currentView);

  const handleNavigate = (view: string) => {
    appNavigate(view);
  };

  // Parse team ID from view if viewing specific team
  const getTeamId = () => {
    if (currentView.startsWith('team-')) {
      return currentView.replace('team-', '');
    }
    return undefined;
  };

  // Parse replica ID for editing
  const getReplicaId = () => {
    if (currentView.startsWith('edit-replica-')) {
      return currentView.replace('edit-replica-', '');
    }
    return undefined;
  };

  const renderView = () => {
    // Handle team-specific views
    if (currentView.startsWith('team-')) {
      return <TeamsView onNavigate={handleNavigate} selectedTeamId={getTeamId()} />;
    }

    // Handle replica edit views
    if (currentView.startsWith('edit-replica-')) {
      return (
        <CreateReplicaPanel
          isDashboardMode={true}
          replicaId={getReplicaId()}
          onReplicaActionComplete={() => handleNavigate('dashboard')}
        />
      );
    }

    if (currentView.startsWith('training-')) {
      const replicaId = currentView.replace('training-', '');
      return (
        <ReplicaTrainingPage
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle HCIP Sovereign Core Setup views
    if (currentView.startsWith('cognitive-setup-')) {
      const replicaId = currentView.replace('cognitive-setup-', '');
      return (
        <SovereignCoreSetup
          replicaId={replicaId}
          onComplete={() => handleNavigate('dashboard')}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle Cognitive Lab views (HCIP Phase 2)
    if (currentView.startsWith('cognitive-lab-')) {
      const replicaId = currentView.replace('cognitive-lab-', '');
      return (
        <CognitiveLab
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle Skill Library views (HCIP Phase 3)
    if (currentView.startsWith('skill-library-')) {
      const replicaId = currentView.replace('skill-library-', '');
      return (
        <SkillLibrary
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle chat views
    if (currentView.startsWith('chat-')) {
      const replicaId = currentView.replace('chat-', '');
      return (
        <ReplicaChatView
          replicaId={replicaId}
          onBack={() => handleNavigate('messages')}
          onNavigate={handleNavigate}
        />
      );
    }

    // Handle social R2R chat views
    if (currentView.startsWith('social-chat-')) {
      const conversationId = currentView.replace('social-chat-', '');
      return (
        <ReplicaToReplicaChatView
          conversationId={conversationId}
          onBack={() => handleNavigate('social')}
        />
      );
    }

    // Handle agent tools views
    if (currentView.startsWith('tools-')) {
      const replicaId = currentView.replace('tools-', '');
      return (
        <AgentToolsPanel
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle identity links views
    if (currentView.startsWith('identity-')) {
      const replicaId = currentView.replace('identity-', '');
      return (
        <IdentityLinksPanel
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle MCP tools views
    if (currentView.startsWith('mcp-tools-')) {
      const replicaId = currentView.replace('mcp-tools-', '');
      return (
        <McpToolsPanel
          replicaId={replicaId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }

    // Handle session memory browser views (HR-BREV-020)
    if (currentView.startsWith('memory-')) {
      const replicaId = currentView.replace('memory-', '');
      return (
        <SessionMemoryView
          replicaId={replicaId}
          replicaName="Replica"
          onBack={() => handleNavigate(`chat-${replicaId}`)}
        />
      );
    }

    // Handle employment detail views
    if (currentView.startsWith('employment-')) {
      const workspaceId = currentView.replace('employment-', '');
      return (
        <EmploymentWorkspaceDetail
          workspaceId={workspaceId}
          onBack={() => handleNavigate('dashboard')}
        />
      );
    }


    switch (currentView) {
      case 'dashboard':
        // DashboardRouter lives inside WorkspaceProvider, so it reacts to workspace switches
        return <DashboardRouter onNavigate={handleNavigate} />;

      // ━━━ Sub-views under DASHBOARD (Govern Mode) ━━━
      case 'my-replica':
        return <MyReplicaView onNavigate={handleNavigate} />;
      case 'company-replicas':
        return <CompanyReplicasView onNavigate={handleNavigate} />;
      case 'teams':
        return <TeamsView onNavigate={handleNavigate} />;
      case 'hierarchy':
      case 'org-chart':
        return <AugmentedOrgChart onNavigate={handleNavigate} />;
      case 'marketplace':
        return <HiringFlowView onNavigate={handleNavigate} />;
      case 'replica-directory':
        return <Marketplace />;
      case 'create-replica':
        return (
          <ReplicaCreationRitual
            onComplete={(replicaId) => {
              if (replicaId) {
                handleNavigate(`edit-replica-${replicaId}`);
              } else {
                handleNavigate('dashboard');
              }
            }}
            onCancel={() => handleNavigate('dashboard')}
          />
        );
      case 'escalation-queue':
        return <EscalationQueuePage />;
      case 'task-management':
        return <TaskManagementPage onBack={() => handleNavigate('dashboard')} />;
      case 'training-rounds':
        return (
          <DailyProofPanel
            onBack={() => handleNavigate('dashboard')}
          />
        );
      case 'deploy':
        return <DashboardRouter onNavigate={handleNavigate} />;
      case 'governance':
        return <DataPermissionsView onNavigate={handleNavigate} />;
      case 'create-team':
        return <TeamsView onNavigate={handleNavigate} />;
      case 'brain':
        return <BrainComparison onBack={() => handleNavigate('dashboard')} />;
      case 'employee-accounts':
        return <EmployeeLinkingView onNavigate={handleNavigate} />;
      case 'cognitive-merge':
        return <CognitiveMergeView onNavigate={handleNavigate} />;

      // ━━━ COMMAND CENTER (Operate Mode) ━━━
      case 'command-center':
      case 'work':
      case 'daily-routines':
      case 'work-mode':
        return <CommandCenterPage onNavigate={handleNavigate} />;
      case 'computer-control':
        return <CommandCenterPage onNavigate={handleNavigate} />;
      case 'messages':
        return <MessagesView onNavigate={handleNavigate} />;

      // ━━━ SOCIAL (Replica Network) ━━━
      case 'social':
        return <SocialNetworkView onNavigate={handleNavigate} />;

      // ━━━ FEED SYNTHESIS (Social Media & News) ━━━
      case 'feed-synthesis':
        return <SocialSynthesisView onNavigate={handleNavigate} />;

      // ━━━ BRIEFCASE (Agent Document Workspace) ━━━
      case 'briefcase':
        return <BriefcaseView onNavigate={handleNavigate} />;

      // ━━━ DATA (Memory & Sovereignty) ━━━
      case 'data':
      case 'my-data':
        return <MyDataView onNavigate={handleNavigate} />;
      case 'permissions':
        return <DataPermissionsView onNavigate={handleNavigate} />;
      case 'audit-logs':
        return <AuditLogViewer onBack={() => handleNavigate('data')} />;

      // ━━━ SETTINGS (Infrastructure) ━━━
      case 'settings':
        return <SettingsView onNavigate={handleNavigate} />;
      case 'identity-verification':
        return <IdentityVerificationView onBack={() => handleNavigate('settings')} />;
      case 'integrations':
        return <IntegrationsCatalogView onNavigate={handleNavigate} />;
      case 'billing':
        return <BillingView onNavigate={handleNavigate} />;
      case 'new-workspace':
        return <CreateWorkspaceView onNavigate={handleNavigate} />;
      case 'join-workspace':
        return <JoinWorkspaceView onNavigate={handleNavigate} />;

      // ━━━ Public / Legal Pages ━━━
      case 'pricing':
        return <PricingPage onBack={() => handleNavigate('dashboard')} onOpenAuthModal={() => { }} />;
      case 'privacy':
        return <PrivacyPolicy onBack={() => handleNavigate('dashboard')} />;

      case 'trust':
        return <TrustCenter onBack={() => handleNavigate('dashboard')} />;
      case 'contact':
        return <Contact />;
      default:
        return <DashboardRouter onNavigate={handleNavigate} />;
    }
  };

  // If viewing 'home', render the marketing site full-screen (no sidebar)
  if (currentView === 'home') {
    return (
      <WorkspaceProvider>
        <ChatProvider>
          <MarketingSite onOpenAuthModal={() => { }} onGoToDashboard={() => handleNavigate('dashboard')} />
        </ChatProvider>
      </WorkspaceProvider>
    );
  }

  // Payment success page – full-screen, no sidebar
  if (currentView === 'payment-success') {
    return <PaymentSuccessPage onNavigate={handleNavigate} />;
  }

  return (
    <WorkspaceProvider>
      <ChatProvider>
        <TutorialProvider onNavigate={handleNavigate}>
          <AppLayout currentView={currentView} onNavigate={handleNavigate} isFullBleed={currentView.startsWith('chat-') || currentView.startsWith('social-chat-')}>
            <ErrorBoundary>
              {renderView()}
            </ErrorBoundary>
          </AppLayout>
          <TutorialWizard />
          <SuperAdminPanel onNavigate={handleNavigate} />
          <SovereignOrb />
        </TutorialProvider>
      </ChatProvider>
    </WorkspaceProvider>
  );
};

// Marketing Site (public)
const MarketingSite: React.FC<{
  onOpenAuthModal: (mode: 'login' | 'signup') => void;
  onGoToDashboard?: () => void;
}> = ({ onOpenAuthModal, onGoToDashboard }) => {
  return (
    <div className="min-h-screen font-inter flex flex-col relative overflow-hidden">
      {/* Neutral Abstract Gradient Background - macOS-inspired */}
      <div className="fixed inset-0 z-0 overflow-hidden bg-hig-bg-primary">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:opacity-100 opacity-0"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 dark:opacity-0 opacity-100"></div>
        {/* Subtle noise texture overlay for depth */}
        <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]"></div>
      </div>

      <Header navItems={staticNavItems} onOpenAuthModal={onOpenAuthModal} onGoToDashboard={onGoToDashboard} />

      <main className="flex-1 relative z-10">
        <Hero onOpenAuthModal={onOpenAuthModal} />
        <ValuePropositions />
        <WhatIsReplica />
        <UseCases />
        <Marketplace onOpenAuthModal={onOpenAuthModal} />
        <IntegrationsShowcase />
      </main>

      <Footer />
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WAITLIST GATE — blocks all routes except allowed public pages
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const WaitlistGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const ALLOWED_PATHS = ['/trust', '/privacy', '/whitepaper', '/manifesto', '/admin'];
  if (WAITLIST_MODE && ALLOWED_PATHS.includes(location.pathname)) {
    return <>{children}</>;
  }
  if (WAITLIST_MODE) {
    return (
      <>
        <WaitlistPage />
        <CookieConsentBanner />
      </>
    );
  }
  return <>{children}</>;
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AUTH GATE — redirects unauthenticated users to home for /app/* routes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen bg-hig-bg-primary flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-hig-accent border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-hig-text-tertiary font-medium tracking-wide">Initializing...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Admin page with auth modal (waitlist mode only)
const AdminPage: React.FC = () => {
  const navigate = useAppNavigate();
  return (
    <>
      <div className="min-h-screen bg-hig-bg-primary flex items-center justify-center">
        <div className="text-center">
          <p className="text-hig-text-secondary text-sm mb-4">Admin access</p>
        </div>
      </div>
      <AuthModal isOpen={true} onClose={() => navigate('home')} initialMode="login" />
      <CookieConsentBanner />
    </>
  );
};

const AppContent: React.FC = () => {
  const { user, isLoading, logout } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  const openAuthModal = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-hig-bg-primary flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-hig-accent border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-hig-text-tertiary font-medium tracking-wide">Initializing...</p>
        </div>
      </div>
    );
  }

  // Authenticated users → dashboard routes are handled by /app/* below
  // For public pages, logged-in users still see them normally

  return (
    <WaitlistGate>
      <Routes>
        {/* ━━━ Public Routes ━━━ */}
        <Route path="/privacy" element={<><PrivacyPolicy onBack={() => window.history.back()} />{!user && <Footer />}<CookieConsentBanner /></>} />
        <Route path="/trust" element={<><TrustCenter onBack={() => window.history.back()} />{!user && <Footer />}<CookieConsentBanner /></>} />
        <Route path="/contact" element={<><Contact /><Footer /><CookieConsentBanner /></>} />
        <Route path="/pricing" element={<><PricingPage onBack={() => window.history.back()} onOpenAuthModal={openAuthModal} /><Footer /><CookieConsentBanner /></>} />
        <Route path="/brain" element={<><BrainComparison onBack={() => window.history.back()} onOpenAuthModal={openAuthModal} /><Footer /><CookieConsentBanner /></>} />
        <Route path="/whitepaper" element={<><WhitepaperPage /><CookieConsentBanner /></>} />
        <Route path="/manifesto" element={<><ManifestoPage /><CookieConsentBanner /></>} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/squircle-demo" element={
          <Suspense fallback={<div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#22D3EE] border-t-transparent rounded-full animate-spin"></div></div>}>
            <SquircleDashboardDemo />
          </Suspense>
        } />
        <Route path="/payment-success" element={<PaymentSuccessPage onNavigate={() => {}} />} />

        {/* ━━━ Dashboard Routes (auth required) ━━━ */}
        <Route path="/app/*" element={
          <AuthGate>
            <DashboardApp onLogout={logout} />
          </AuthGate>
        } />

        {/* ━━━ Home / Catch-all ━━━ */}
        <Route path="/" element={
          user
            ? <Navigate to="/app" replace />
            : <><MarketingSite onOpenAuthModal={openAuthModal} /><AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialMode={authMode} /><CookieConsentBanner /></>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </WaitlistGate>
  );
};

function App() {
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  return (
    <BrowserRouter>
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey || ''}>
        <ThemeProvider>
          <AuthProvider>
            <ReplicaProvider>
              <AppContent />
            </ReplicaProvider>
          </AuthProvider>
        </ThemeProvider>
      </GoogleReCaptchaProvider>
    </BrowserRouter>
  );
}

export default App;